import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../components/AuthProvider';
import { LayoutContext } from '../components/LayoutProvider';
import { showTemporaryMessage } from "../components/Message";
import logoicon from '../images/tuutuum-logo-icon.png';
import { update_scan_token, get_scan_tokens, get_bearers } from "../tools-network";
import { validate_name } from "../tools";
import NeumorphicContainer from './NeumorphicContainer';

const RegisterQrCodeStep3 = ({ selectedBearerId, suggestedName, onPrevious, onNext }) => {
    const { t } = useTranslation();
    const { loginToken, userId } = React.useContext(AuthContext);
    const { setIsHeaderVisible } = React.useContext(LayoutContext);

    useEffect(() => {
        setIsHeaderVisible(false);

        return () => setIsHeaderVisible(true);
    }, [setIsHeaderVisible]);

    const [name, setName] = useState('');
    const [isCheckingName, setIsCheckingName] = useState(false);
    const [existingWristbands, setExistingWristbands] = useState([]);
    const [nameWarning, setNameWarning] = useState('');

    // Fetch ALL wristbands for ALL bearers of this user for duplicate checking
    useEffect(() => {
        const fetchAllUserWristbands = async () => {
            try {
                // Get all bearers for this user
                const bearersRes = await get_bearers(loginToken, userId);
                if (bearersRes.status && bearersRes.data) {
                    const bearerIds = Object.keys(bearersRes.data);
                    
                    // Fetch wristbands for all bearers
                    const allWristbandPromises = bearerIds.map(bearerId => 
                        get_scan_tokens(loginToken, bearerId)
                    );
                    const allWristbandsResults = await Promise.all(allWristbandPromises);
                    
                    // Collect all wristband names
                    const allNames = [];
                    allWristbandsResults.forEach(res => {
                        if (res.status && res.data) {
                            res.data.forEach(token => {
                                if (token.name) {
                                    allNames.push(token.name);
                                }
                            });
                        }
                    });
                    
                    setExistingWristbands(allNames);
                }
            } catch (err) {
                console.log("Error fetching user's wristbands: " + err);
            }
        };
        
        if (userId) {
            fetchAllUserWristbands();
        }
    }, [loginToken, userId]);

    // Generate unique wristband name based on suggestion
    useEffect(() => {
        const generateUniqueName = async () => {
            if (!suggestedName || existingWristbands.length === 0) {
                if (suggestedName) {
                    setName(suggestedName);
                }
                return;
            }
            
            setIsCheckingName(true);
            try {
                const existingNames = existingWristbands.map(n => n.toLowerCase());
                
                // Check if suggested name is available
                let finalName = suggestedName;
                let counter = 2;
                
                while (existingNames.includes(finalName.toLowerCase())) {
                    finalName = `${suggestedName} ${counter}`;
                    counter++;
                }
                
                setName(finalName);
            } catch (err) {
                console.log("Error generating unique name: " + err);
                setName(suggestedName);
            } finally {
                setIsCheckingName(false);
            }
        };
        
        generateUniqueName();
    }, [suggestedName, existingWristbands]);

    // Check for duplicate name when user types
    useEffect(() => {
        if (name.trim() && existingWristbands.length > 0) {
            const isDuplicate = existingWristbands.some(
                existingName => existingName.toLowerCase() === name.trim().toLowerCase()
            );
            if (isDuplicate) {
                setNameWarning(t("wristband_name_already_exists"));
            } else {
                setNameWarning('');
            }
        } else {
            setNameWarning('');
        }
    }, [name, existingWristbands, t]);

    const handlePrevious = async () => {
        console.log("Handle previous!");
        onPrevious();
    };

    const handleNext = async () => {
        if (!validate_name(name)) {
            showTemporaryMessage(t("name_is_not_valid"), t("name_contains_invalid_characters"), 'danger');
            return;
        }
        if (nameWarning) {
            showTemporaryMessage(t("wristband_name_already_exists"), t("please_choose_different_name"), 'warning');
            return;
        }
        console.log("Handle Next!");
        setScanTokenName();
        onNext();
    };

    const queryParams = new URLSearchParams(location.search);
    const pending_token = queryParams.get('pending_token');

    const setScanTokenName = async () => {
        try {
            const updateFields = { name: name };
            const res = await update_scan_token(loginToken, pending_token, updateFields);
            if (res.success) {
                showTemporaryMessage(t("scan_token_name_successfully_updated"), "", 'success');
            } else {
                console.log("UPDATE SCAN TOKEN FAILED: " + JSON.stringify(res));
                // Check if it's a duplicate name error (409 Conflict)
                if (res.msg && res.msg.includes('Name already exists')) {
                    showTemporaryMessage(t("wristband_name_already_exists"), t("please_choose_different_name"), 'warning');
                }
                return;
            }
        } catch (err) {
            console.log("Error updating scan code: " + err);
            showTemporaryMessage("Scan token information failed to update!", " ", 'fail');
            return;
        }
    };

    return (
        <NeumorphicContainer>
            <img src={logoicon} alt="Tuutuum Logo" className="block h-7 w-auto" />
            <h2 className="text-center text-2xl leading-tight px-3">
                {t("give_wristband_name")}
            </h2>
            {/* Input wristband name */}
            <div className="flex flex-col w-full gap-1 justify-stretch pt-1">
                <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t("annas_first_wristband")}
                    className={`block rounded-md border w-full px-2 py-2 text-gray-900 shadow-sm focus:outline-none focus:ring focus:ring-cyan-600 focus:border-cyan-600 placeholder-gray-400 sm:text-sm sm:leading-4 ${
                        nameWarning ? 'border-yellow-500' : 'border-gray-300'
                    }`}
                />
                {nameWarning && (
                    <p className="text-xs text-yellow-600 mt-1">
                        {nameWarning}
                    </p>
                )}
            </div>
            {/* Buttons */}
            <div className="flex flex-nowrap w-full gap-4 justify-between">
                <button
                    className="btn-secondary btn-secondary:hover text-white px-3 py-1 w-auto rounded-md sm:leading-6 text-center font-semibold text-sm transition ease-in-out hover:scale-105"
                    onClick={handlePrevious}
                >
                    {t("previous")}
                </button>
                <button
                    className="btn-main btn-main:hover text-white px-3 py-1 w-auto rounded-md sm:leading-6 text-center font-semibold text-sm transition ease-in-out hover:scale-105 disabled:bg-gray-400 disabled:text-grey-600 disabled:cursor-not-allowed"
                    disabled={name === '' || nameWarning !== ''}
                    onClick={handleNext}
                >
                    {t("next")}
                </button>
            </div>
        </NeumorphicContainer>
    );
}

export default RegisterQrCodeStep3;